import { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAllFeedsHook, useDirectMessagesHook, useAnnouncementsHook, useTabNavigation } from './index';
import { TAB_NAMES, NAVIGATION_EVENTS, SOCKET_EVENTS, FEED_TYPE } from '../constants';
import { eventEmitter } from '../../../services/event-emitter-service';
import { useManageFeedsHook } from './manage-feeds/useManageFeedsHook';
import { getTotalUnreadCount } from '../mappers/feedUtility';
import { getUnreadCounts, joinFeedRoom } from '../services/Feed.service';
import { mapUnreadCounts } from '../mappers/feedUtility';

/**
 * Unified hook that manages both All Feeds and Direct Messages
 * This prevents unnecessary re-renders by controlling when each hook is active
 * 
 * CENTRALIZED SOCKET EVENT HANDLING:
 * - Single socket listener in this manager
 * - Routes events to appropriate hooks based on feed type and active tab
 * - Prevents duplicate processing and unnecessary re-renders
 */
export const useFeedsManager = () => {
  // Get user details from Redux store
  const { userDetail } = useSelector((state) => state.app || {});
  const currentUserKey = userDetail?.userId;

  // unread counts 
  const [unreadCounts, setUnreadCounts] = useState(mapUnreadCounts(null));

  // Compute total unread count from individual counts
  const totalUnreadCount = useMemo(() => {
    return getTotalUnreadCount(unreadCounts)
  }, [unreadCounts]);

  // Tab management
  const tabNavigation = useTabNavigation(TAB_NAMES.MESSAGES);

  // Determine which hooks should be active
  const isAllFeedsActive = tabNavigation.isTabActive(TAB_NAMES.ALL);
  const isMessagesActive = tabNavigation.isTabActive(TAB_NAMES.MESSAGES);
  const isAnnouncementsActive = tabNavigation.isTabActive(TAB_NAMES.ANNOUNCEMENTS);

  // Refs to store hook handlers for socket events
  const allFeedsHandlersRef = useRef({});
  const directMessagesHandlersRef = useRef({});
  const announcementsHandlersRef = useRef({});
  const openConversationRef = useRef();

  // Ref to store pending conversation data when navigating from All Feeds to Messages
  const pendingConversationRef = useRef(null);



  // Navigation handler for All Feeds
  const handleNavigateToMessages = useCallback(
    (eventType, data) => {
      switch (eventType) {
        case NAVIGATION_EVENTS.NAVIGATE_TO_DM:
          tabNavigation.navigateToTab(TAB_NAMES.MESSAGES);
          // Store the conversation data to be opened when DM hook becomes active
          if (data) {
            pendingConversationRef.current = data;
          }
          break;
        default:
          console.warn('[FeedsManager] Unknown navigation event:', eventType);
      }
    },
    [tabNavigation]
  );

  // Function to decrement unread count when conversation is opened
  const decrementUnreadCount = (feedType, count) => {
    if (count <= 0) return; // Nothing to decrement
    
    switch (feedType?.toUpperCase()) {
      case FEED_TYPE.DM:
        setUnreadCounts(prev => ({
          ...prev,
          unreadMessagesCount: Math.max(0, prev.unreadMessagesCount - count)
        }));
        break;
      case FEED_TYPE.ANNOUNCEMENT:
        setUnreadCounts(prev => ({
          ...prev,
          unreadAnnouncementsCount: Math.max(0, prev.unreadAnnouncementsCount - count)
        }));
        break;
      case FEED_TYPE.CHANNEL:
        setUnreadCounts(prev => ({
          ...prev,
          unreadChannelsCount: Math.max(0, prev.unreadChannelsCount - count)
        }));
        break;
      default:
        break;
    }
  };

  // Helper: increment unread count when new message arrives and conversation is not open
  const incrementUnreadCount = (feedType) => {
    switch ((feedType || '').toUpperCase()) {
      case FEED_TYPE.DM:
        setUnreadCounts(prev => ({
          ...prev,
          unreadMessagesCount: prev.unreadMessagesCount + 1
        }));
        break;
      case FEED_TYPE.ANNOUNCEMENT:
        setUnreadCounts(prev => ({
          ...prev,
          unreadAnnouncementsCount: prev.unreadAnnouncementsCount + 1
        }));
        break;
      case FEED_TYPE.CHANNEL:
        setUnreadCounts(prev => ({
          ...prev,
          unreadChannelsCount: prev.unreadChannelsCount + 1
        }));
        break;
      default:
        break;
    }
  };

  // Initialize hooks with proper active states
  const allFeedsHook = useAllFeedsHook(handleNavigateToMessages, isAllFeedsActive);
  const directMessagesHook = useDirectMessagesHook(tabNavigation.handleBackToOriginalTab, isMessagesActive, decrementUnreadCount, tabNavigation.activeTab);
  const announcementsHook = useAnnouncementsHook(isAnnouncementsActive, decrementUnreadCount, incrementUnreadCount);

  // Store hook handlers in refs for socket event routing
  allFeedsHandlersRef.current = {
    onFeedCreated: allFeedsHook.onFeedCreated,
    onMessageReceived: allFeedsHook.onMessageReceived,
  };

  directMessagesHandlersRef.current = {
    onConversationCreated: directMessagesHook.onConversationCreated,
    onMessageInConversation: directMessagesHook.onMessageInConversation,
  };

  announcementsHandlersRef.current = {
    onAnnouncementCreated: announcementsHook.onAnnouncementCreated,
    onAnnouncementMessageReceived: announcementsHook.onAnnouncementMessageReceived,
  };

  // Ref to store the current openConversation function
  openConversationRef.current = directMessagesHook.openConversation;

  // Handle FEED_CREATED events
  const handleFeedCreatedEvent = useCallback((event_data, feedType) => {
    // Handle Direct Message feed creation
    if (feedType === FEED_TYPE.DM) {
      joinFeedRoom([`feed_${event_data.feed_id}`]);
      // Update UI if Messages tab is active
      if ((isMessagesActive || isAnnouncementsActive) && directMessagesHandlersRef.current.onConversationCreated) {
        directMessagesHandlersRef.current.onConversationCreated(event_data);
      }
      
    }
    // Handle Announcement feed creation
    else if (feedType === FEED_TYPE.ANNOUNCEMENT) {
      if ((isAnnouncementsActive) && announcementsHandlersRef.current.onAnnouncementCreated) {
        announcementsHandlersRef.current.onAnnouncementCreated(event_data);
      }
      else if(isMessagesActive || isAllFeedsActive) {
        incrementUnreadCount(FEED_TYPE.ANNOUNCEMENT);
      }
    }
  
  }, [isAllFeedsActive, isMessagesActive, isAnnouncementsActive]);

  // Handle FEED_MESSAGE_SENT events
  const handleFeedMessageSentEvent = useCallback((event_data, feedType) => {
    // Check if this message is for the currently open conversation (only applies to DMs)
    const messageConversationId = String(event_data.feed_id || '');
    const currentlyOpenConversationId = directMessagesHook.selectedConversationRef?.current?.id;
    const isConversationOpen = currentlyOpenConversationId != null && String(currentlyOpenConversationId) === messageConversationId;

    // Handle Direct Message
    if (feedType === FEED_TYPE.DM) {
      // Increment unread count ONLY if conversation is NOT open
      if (!isConversationOpen) {
        incrementUnreadCount(FEED_TYPE.DM);
      }
      // Always update Direct Messages hook state for data consistency
      if (directMessagesHandlersRef.current.onMessageInConversation) {
        directMessagesHandlersRef.current.onMessageInConversation(event_data);
      }
      // Update All Feeds if active
      if (isAllFeedsActive && allFeedsHandlersRef.current.onMessageReceived) {
        allFeedsHandlersRef.current.onMessageReceived(event_data);
      }
    }

  }, [isAllFeedsActive, directMessagesHook.selectedConversationRef]);

  // CENTRALIZED SOCKET EVENT HANDLER
  const handleSocketEvent = useCallback((eventData) => {
    const { event_name, event_data } = eventData;

    if (!event_name || !event_data) {
      console.warn('[FeedsManager] Invalid socket event data:', eventData);
      return;
    }

    const feedType = ((event_data?.feed_type || event_data?.metadata?.feed_type || event_data?.message_type) ?? '').toUpperCase();
    const isOwnMessage = event_data?.sender_id === currentUserKey;

    // Route events based on event type, feed type, and active tab
    switch (event_name) {
      case SOCKET_EVENTS.FEED_CREATED:
        handleFeedCreatedEvent(event_data, feedType);
        break;

      case SOCKET_EVENTS.FEED_MESSAGE_SENT:
        if (isOwnMessage) {
          console.log('[FeedsManager] Skipping own message');
          return;
        }
        handleFeedMessageSentEvent(event_data, feedType);
        break;

      default:
        console.warn('[FeedsManager] Unhandled socket event:', event_name);
    }
  }, [currentUserKey, handleFeedCreatedEvent, handleFeedMessageSentEvent]);

  // Set up centralized socket listener
  useEffect(() => {
    eventEmitter.on(SOCKET_EVENTS.COMMUNICATION_EVENT, handleSocketEvent);
    return () => eventEmitter.off(SOCKET_EVENTS.COMMUNICATION_EVENT, handleSocketEvent);
  }, [handleSocketEvent]);

  // Handle pending conversation when Messages tab becomes active
  useEffect(() => {
    if (isMessagesActive && pendingConversationRef.current) {
      const conversationData = pendingConversationRef.current;
      pendingConversationRef.current = null; // Clear after use
      openConversationRef.current(conversationData);
    }
  }, [isMessagesActive]);

  // Load unread counts when the module loads
  useEffect(() => {
    const loadUnreadCounts = async () => {
      console.log('[FeedsManager] Loading unread counts...');
      const unreadCounts = await getUnreadCounts();
      setUnreadCounts((prevCounts) => ({
        ...prevCounts,
        ...unreadCounts
      }));
    };

    loadUnreadCounts();
  }, []); // Empty dependency array ensures this runs only once when module loads

  // Memoize DM props to prevent unnecessary re-renders
  const dmProps = useMemo(
    () => ({
      userDetail: directMessagesHook.userDetail,
      feedDetails: directMessagesHook.feedDetails,
      isLoading: directMessagesHook.isLoading,
      feeds: directMessagesHook.conversations,
      sortBy: directMessagesHook.sortBy,
      showEditor: directMessagesHook.showEditor,
      messageView: directMessagesHook.messageView,
      showSettingsIcon: directMessagesHook.showSettingsIcon,
      showLoadMore: directMessagesHook.showLoadMore,
      totalConversationCount: directMessagesHook.totalConversationCount,
      showNewMessageBtn: directMessagesHook.showNewMessageBtn,
      openConversationRef: openConversationRef.current,
      searchText: directMessagesHook.searchQuery,
      handleFeedDetailsLoaded: directMessagesHook.handleFeedDetailsLoaded,
      onNewMessageHandler: directMessagesHook.onNewMessageHandler,
      onSortChangeHandler: directMessagesHook.onSortChangeHandler,
      onSearchHandler: directMessagesHook.onSearchHandler,
      onLoadMore: directMessagesHook.onLoadMore,
      setConversations: directMessagesHook.setConversations,
      refreshFeedDetails: directMessagesHook.refreshFeedDetails,
      readReceiptsModal: directMessagesHook.readReceiptsModal,
    }),
    [
      directMessagesHook.userDetail,
      directMessagesHook.feedDetails,
      directMessagesHook.isLoading,
      directMessagesHook.conversations,
      directMessagesHook.sortBy,
      directMessagesHook.showEditor,
      directMessagesHook.messageView,
      directMessagesHook.showLoadMore,
      directMessagesHook.totalConversationCount,
      directMessagesHook.showNewMessageBtn,
      openConversationRef.current,
      directMessagesHook.searchQuery,
      directMessagesHook.handleFeedDetailsLoaded,
      directMessagesHook.onNewMessageHandler,
      directMessagesHook.onSortChangeHandler,
      directMessagesHook.onSearchHandler,
      directMessagesHook.onLoadMore,
      directMessagesHook.setConversations,
      directMessagesHook.readReceiptsModal
    ]
  );

  // Memoize All Feeds props
  const allFeedsProps = useMemo(
    () => ({
      isLoading: allFeedsHook.isLoading,
      feeds: allFeedsHook.feeds,
      totalFeedCount: allFeedsHook.totalFeedCount,
      defaultFeedSize: allFeedsHook.pageSize,
      pagination: {
        page: allFeedsHook.currentPage,
        onPageChange: allFeedsHook.onPageChange,
      },
      onFeedSizeChangeHandler: allFeedsHook.onPageSizeChange,
      onFeedSelection: allFeedsHook.onFeedClick,
    }),
    [
      allFeedsHook.isLoading,
      allFeedsHook.feeds,
      allFeedsHook.totalFeedCount,
      allFeedsHook.pageSize,
      allFeedsHook.currentPage,
      allFeedsHook.onPageChange,
      allFeedsHook.onPageSizeChange,
      allFeedsHook.onFeedClick,
    ]
  );

  // Get manage feed props (contains onCreateAnnouncement) - must come after dmProps
  // Pass refetchAnnouncements so it can be called after creating announcement
  const manageFeedProps = useManageFeedsHook(dmProps, tabNavigation, announcementsHook.refetchAnnouncements);
  // Wire announcement handler to actual create announcement logic
  const announcementsWithHandler = useMemo(
    () => ({
      ...announcementsHook,
      onNewAnnouncementHandler: manageFeedProps.onCreateAnnouncement,
      onAnnouncementDeleteHandler: announcementsHook.onAnnouncementDeleteHandler,
    }),
    [announcementsHook, manageFeedProps.onCreateAnnouncement]
  );


  const manageFeedPermissions = useMemo(() => {
    if (tabNavigation.activeTab === 'Messages') {
      return [
        {
          count: 1,
          roleName: 'Learner'
        }
      ];
    }
    return [];
  }, [tabNavigation.activeTab]);
  
  return {
    //Manage Feed Permission
    manageFeedPermissions,

    // Tab management
    tabNavigation,

    // Unread counts
    unreadCounts,
    totalUnreadCount,
    // All Feeds
    allFeeds: allFeedsProps,

    // Direct Messages
    dm: dmProps,
    messageViewRef: directMessagesHook.messageViewRef,
    // Manage Feed
    manageFeedProps,
    // Announcements (now using real API data with working handler)
    announcements: announcementsWithHandler,
    channels: {
      isLoading: false,
      totalChannelCount: [],
      selectedType: 'All Channels',
      allChannelText: 'All Channels',
      myChannelText: 'My Channels',
      sortBy: 'MostRecentActivity',
      feeds: [],
      showLoadMore: false,
      showCreateNewChannelButton: false,
      onChannelTypeChangeHandler: () => null,
      onSortChangeHandler: () => null,
      onLoadMore: () => null,
      onSearchHandler: () => null,
      onNewChannelHandler: () => null,
    },
  };
};
